import {
  ApolloClient,
  ApolloLink,
  type FieldFunctionOptions,
  HttpLink,
  InMemoryCache,
  ApolloProvider as Provider,
  type Reference,
  type StoreObject,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { signOut as signOutAuth } from "aws-amplify/auth";
import { useTenantId } from "hooks/useTenant";
import { PropsWithChildren, useEffect, useLayoutEffect, useMemo } from "react";

import { metricsLogger } from "../utils/metricsLogger";
import { useToken } from "./token";

const removeTypenameLink = removeTypenameFromVariables();
const SCOUT_DASHBOARD_METRICS_OPERATION_NAME = "ScoutDashboardMetrics";

// Logging link for performance diagnostics - logs all GraphQL requests to metricsLogger
// Uses wide event format for comprehensive tracking
const metricsLink = new ApolloLink((operation, forward) => {
  const startTime = performance.now();
  const operationName = operation.operationName;

  if (operationName === SCOUT_DASHBOARD_METRICS_OPERATION_NAME) {
    // This dashboard polls large metric payloads continuously. In local dev, the
    // metrics logger keeps query events in memory, so logging every poll can
    // look like an Apollo cache leak during long dashboard sessions.
    return forward(operation);
  }

  const variables = operation.variables;
  const context = operation.getContext();
  const fetchPolicy = (context.fetchPolicy as string) ?? "cache-first";

  // Log query start with wide events
  metricsLogger.logQueryStart({
    queryName: operationName,
    variables,
    fetchPolicy,
  });

  return forward(operation).map((response) => {
    const duration = performance.now() - startTime;

    // Estimate response size for diagnostics
    let responseSize = 0;
    try {
      responseSize = JSON.stringify(response).length;
    } catch {
      // Ignore serialization errors
    }

    // Log query resolve with wide events
    metricsLogger.logQueryResolve({
      queryName: operationName,
      duration,
      variables,
      responseSize,
    });

    return response;
  });
});

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/graphql`
    : "http://localhost:4000/graphql",
});

type ScoutConversationCacheItem = Reference | StoreObject;

type ScoutConversationsCacheValue = {
  __typename?: string;
  count?: number;
  items?: readonly ScoutConversationCacheItem[];
};

/**
 * De-dupes merged conversation rows by `id` after Apollo combines paginated
 * `fetchMore` results into a single cached list for infinite scroll.
 *
 * Apollo's pagination docs recommend a field-policy `merge` function so
 * follow-up pages can be combined in cache instead of stored as separate
 * results:
 * https://www.apollographql.com/docs/react/pagination/core-api
 *
 * We additionally de-dupe by `id` because page-based requests can overlap
 * during refetches or when rows move between offsets, and the dialog should
 * never render the same thread twice.
 */
function dedupeScoutConversationItems(
  items: readonly ScoutConversationCacheItem[],
  { readField }: Pick<FieldFunctionOptions, "readField">
): ScoutConversationCacheItem[] {
  const seenIds = new Set<string>();
  const dedupedItems: ScoutConversationCacheItem[] = [];

  for (const item of items) {
    const id = readField<string>("id", item);

    if (id == null) {
      dedupedItems.push(item);
      continue;
    }

    if (seenIds.has(id)) {
      continue;
    }

    seenIds.add(id);
    dedupedItems.push(item);
  }

  return dedupedItems;
}

/**
 * Custom field-policy merge for `Query.getScoutConversations`.
 *
 * Apollo's offset-pagination guidance uses a `merge` function that places
 * incoming rows at the correct offset instead of blindly appending them:
 * https://www.apollographql.com/docs/react/pagination/core-api
 * https://www.apollographql.com/docs/react/pagination/offset-based
 *
 * We cannot use `offsetLimitPagination()` directly here because this field
 * returns an object (`{ count, items }`) rather than a bare array, so we merge
 * the `items` array manually while still preserving the latest `count` for the
 * infinite-scroll `hasMore` check. The field's `keyArgs` are configured
 * separately so different searches do not share the same cached list:
 * https://www.apollographql.com/docs/react/pagination/key-args
 */
function mergeScoutConversationPage(
  existing: ScoutConversationsCacheValue | undefined,
  incoming: ScoutConversationsCacheValue,
  options: FieldFunctionOptions
): ScoutConversationsCacheValue {
  const page = typeof options.args?.page === "number" ? options.args.page : 0;
  const incomingItems = incoming.items ?? [];

  if (page === 0) {
    return {
      ...incoming,
      items: dedupeScoutConversationItems(incomingItems, options),
    };
  }

  const pageSize =
    typeof options.args?.pageSize === "number" ? options.args.pageSize : incomingItems.length;
  const startIndex = page * pageSize;
  const mergedItems = existing?.items ? [...existing.items] : [];

  for (const [index, item] of incomingItems.entries()) {
    mergedItems[startIndex + index] = item;
  }

  return {
    ...existing,
    ...incoming,
    items: dedupeScoutConversationItems(mergedItems, options),
  };
}

const createErrorLink = () =>
  onError(({ graphQLErrors, networkError }) => {
    // We don't want to redirect to the login page if we are already on the login page
    // that could cause an infinite redirect
    // since / hits the API
    const isOnLoginPage = window.location.pathname === "/";

    const handleAuthError = () => {
      if (!isOnLoginPage) {
        // Mark that we're logging out to prevent redirect loops
        localStorage.setItem("logout", "true");

        // Sign out from AWS Amplify to clear all authentication state
        signOutAuth()
          .then(() => {
            // Force a full page reload to clear any cached state
            window.location.href = "/";
          })
          .catch((signOutError) => {
            console.error("Error during signout:", signOutError);
            // Even if signout fails, still redirect to clear the app state
            window.location.href = "/";
          });
      }
    };

    if (graphQLErrors) {
      graphQLErrors.forEach(({ extensions }) => {
        if (extensions?.code === "UNAUTHENTICATED") {
          handleAuthError();
        }
      });
    }

    if (networkError && "statusCode" in networkError && networkError.statusCode === 401) {
      handleAuthError();
    }
  });

export const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      HdHandler: {
        keyFields: ["handlerId", "activityLocation", "sourceType", "seqNumber"],
      },
      Payment: {
        fields: {
          facilities: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      TierIIReport: {
        fields: {
          assignees: {
            merge(_, incoming) {
              return incoming;
            },
          },
          reviewers: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      Submission: {
        fields: {
          facilitySubmissions: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      // Merge policy for wasteSubmissionsSummary to prevent cache warnings
      Query: {
        fields: {
          getScoutConversations: {
            /**
             * Key the cache entry by the stable filter inputs only so Apollo
             * stores every page for the same search in one list that `merge`
             * can extend for infinite scroll. Including `page` or `pageSize`
             * here would split each fetchMore response into a separate entry.
             */
            keyArgs: ["tenantId", "search", "sort"],
            merge(existing, incoming, options) {
              return mergeScoutConversationPage(existing, incoming, options);
            },
          },
          wasteSubmissionsSummary: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  // Provide initial link to satisfy Apollo Client 4.0 requirement
  // This will be replaced by setLink() in ApolloProvider once auth is available
  link: from([metricsLink, removeTypenameLink, createErrorLink(), httpLink]),
  devtools: {
    enabled: import.meta.env.VITE_ENV !== "release",
  },
});

/**
 * Resolves which tenant ID to send as the x-encamp-tenant-id header.
 *
 * Priority:
 *  1. Explicit override from Apollo context (used by staff-facing pickers
 *     that need to query a specific customer tenant)
 *  2. Empty string for staff routes (allows cross-tenant navigation)
 *  3. The current tenant from useTenantId()
 */
export function resolveTenantIdHeader(
  tenantIdOverride: string | undefined,
  pathname: string,
  currentTenantId: string | undefined
): string {
  if (tenantIdOverride != null) return tenantIdOverride;
  if (pathname.startsWith("/staff")) return "";
  return currentTenantId ?? "";
}

export function ApolloProvider(props: PropsWithChildren<object>) {
  const { getIdTokenRefreshIfExpired, isAuthenticated } = useToken();

  const currentTenantId = useTenantId();

  const tenantLink = useMemo(
    () =>
      setContext((_, previousContext) => {
        // See resolveTenantIdHeader for the full priority description.
        // Callers can pass `{ tenantIdOverride: "<id>" }` as Apollo context
        // to target a specific tenant (used by staff-facing pickers).
        const { headers, tenantIdOverride } = previousContext;
        return {
          headers: {
            ...headers,
            "x-encamp-tenant-id": resolveTenantIdHeader(
              tenantIdOverride,
              window.location.pathname,
              currentTenantId
            ),
          },
        };
      }),
    [currentTenantId]
  );

  // useLayoutEffect (as opposed to useEffect) forces this to be synchronous so it will finish
  // running before the component is rendered. That is necessary because we need to make the
  // client.setLink call before any child components attempt to actually use the client
  useLayoutEffect(() => {
    const authLink = setContext(async (_, { headers }) => {
      const idToken = await getIdTokenRefreshIfExpired();
      return {
        headers: {
          ...headers,
          "Content-Type": "application/json",
          authorization: idToken ? `Bearer ${idToken}` : "",
        },
      };
    });

    client.setLink(
      from([metricsLink, removeTypenameLink, createErrorLink(), authLink, tenantLink, httpLink])
    );
  }, [getIdTokenRefreshIfExpired, tenantLink]);

  useEffect(() => {
    if (!isAuthenticated) {
      client.clearStore();
    }
  }, [isAuthenticated]);

  return <Provider client={client}>{props.children}</Provider>;
}
